import React, { useState } from 'react';
import {
  FiX,
  FiUserPlus,
  FiUsers,
  FiMail,
  FiLock,
  FiUser,
  FiTrendingUp,
  FiArrowDown,
  FiCheckCircle,
  FiPlus,
  FiCalendar,
  FiClock,
  FiDollarSign,
  FiCheck,
  FiSearch,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api';
import { Loader } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { SimpleFormField } from '../Common/FormField';
import { User } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'masters' | 'create' | 'upgrade';

const MasterManagementModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('masters');
  const [newMasterForm, setNewMasterForm] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedTradingType, setSelectedTradingType] = useState<'swing' | 'day' | ''>('');
  const [upgradeTradingType, setUpgradeTradingType] = useState<'swing' | 'day' | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'demote' | 'upgrade' | null>(null);
  const [confirmUserId, setConfirmUserId] = useState('');
  const [confirmUserName, setConfirmUserName] = useState('');

  // Fetch masters
  const { data: mastersData, isLoading: mastersLoading, refetch: refetchMasters } = useQuery({
    queryKey: ['admin', 'masters'],
    queryFn: () => adminApi.getMasters(),
    enabled: isOpen && activeTab === 'masters',
  });

  // Fetch followers for upgrade with pagination and search
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'followers', page, searchQuery],
    queryFn: () => adminApi.getAllUsers({
      page,
      limit: 5,
      role: 'follower',
      search: searchQuery
    }),
    enabled: isOpen && activeTab === 'upgrade',
  });

  // Create master mutation
  const createMasterMutation = useMutation({
    mutationFn: (data: { userId?: string; name?: string; email?: string; password?: string; tradingType?: 'swing' | 'day' }) =>
      adminApi.createMaster(data),
    onSuccess: () => {
      setNewMasterForm({ name: '', email: '', password: '' });
      setSelectedUserId('');
      setSelectedTradingType('');
      setUpgradeTradingType('');
      setShowConfirmModal(false);
      setConfirmAction(null);
      setConfirmUserId('');
      setConfirmUserName('');
      setError('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'masters'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'followers'] });
      refetchMasters();
      setTimeout(() => setActiveTab('masters'), 1500);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || err.message || 'Failed to create master');
    },
  });

  // Demote master mutation
  const demoteMasterMutation = useMutation({
    mutationFn: (userId: string) => adminApi.demoteMaster(userId),
    onSuccess: () => {
      setShowConfirmModal(false);
      setConfirmAction(null);
      setConfirmUserId('');
      setConfirmUserName('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'masters'] });
      refetchMasters();
    },
  });

  const requirePaymentMutation = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      adminApi.setMasterRequireRecurringPayment(userId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'masters'] });
      refetchMasters();
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || err.message || 'Failed to update payment requirement');
    },
  });

  const handleCreateNewMaster = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newMasterForm.name || !newMasterForm.email || !newMasterForm.password) {
      setError('All fields are required');
      return;
    }

    if (newMasterForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!selectedTradingType) {
      setError('Please select a trading type (Swing or Day)');
      return;
    }

    createMasterMutation.mutate({ ...newMasterForm, tradingType: selectedTradingType });
  };

  const handleUpgradeUser = () => {
    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }
    if (!upgradeTradingType) {
      setError('Please select a trading type (Swing or Day)');
      return;
    }
    setError('');
    const selectedFollower = followers.find((user: User) => (user._id || user.id) === selectedUserId);
    setConfirmAction('upgrade');
    setConfirmUserId(selectedUserId);
    setConfirmUserName(selectedFollower?.name || 'this user');
    setShowConfirmModal(true);
  };

  const handleDemoteMaster = (userId: string, name: string) => {
    setConfirmAction('demote');
    setConfirmUserId(userId);
    setConfirmUserName(name);
    setShowConfirmModal(true);
  };

  const handleConfirmAction = () => {
    if (!confirmAction || !confirmUserId) return;

    if (confirmAction === 'demote') {
      demoteMasterMutation.mutate(confirmUserId);
      return;
    }

    if (!upgradeTradingType) {
      setError('Please select a trading type (Swing or Day)');
      setShowConfirmModal(false);
      setConfirmAction(null);
      setConfirmUserId('');
      setConfirmUserName('');
      return;
    }

    createMasterMutation.mutate({ userId: confirmUserId, tradingType: upgradeTradingType });
  };

  if (!isOpen) return null;

  const masters = mastersData?.users || [];
  const followers = usersData?.users || [];
  const pagination = usersData?.pagination;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between gap-3 z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-primary-100 p-2 rounded-lg shrink-0">
              <FiTrendingUp className="lg:h-6 h-5 lg:w-6 w-5 text-primary-600" />
            </div>
            <div className="min-w-0">
              <h2 className="lg:text-2xl text-lg font-bold text-gray-900">Master Trader Management</h2>
              <p className="lg:text-sm text-[12px] text-gray-600">Create and manage master trader accounts</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-4 sm:px-6">
          <nav className="flex gap-4 sm:gap-6 overflow-x-auto whitespace-nowrap">
            <button
              onClick={() => setActiveTab('masters')}
              className={`py-3 border-b-2 transition-colors shrink-0 ${activeTab ==='masters'
                ? 'border-primary-500 text-primary-600 font-semibold'
                : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              <div className="flex items-center gap-2">
                <FiUsers className="h-5 w-5" />
                <span>Active Masters ({masters.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className={`py-3 border-b-2 transition-colors shrink-0 ${activeTab ==='create'
                ? 'border-primary-500 text-primary-600 font-semibold'
                : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              <div className="flex items-center gap-2">
                <FiUserPlus className="h-5 w-5" />
                <span>Create New</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('upgrade')}
              className={`py-3 border-b-2 transition-colors shrink-0 ${activeTab ==='upgrade'
                ? 'border-primary-500 text-primary-600 font-semibold'
                : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              <div className="flex items-center gap-2">
                <FiPlus className="h-5 w-5" />
                <span>Upgrade Follower</span>
              </div>
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Active Masters Tab */}
          {activeTab === 'masters' && (
            <div>
              {mastersLoading ? (
                <Loader message="Loading masters..." />
              ) : masters.length === 0 ? (
                <div className="text-center py-12">
                  <FiUsers className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg mb-2">No master traders yet</p>
                  <p className="text-gray-500 text-sm">Create your first master trader to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {masters.map((master: User) => {
                    const masterId = master._id || master.id!;
                    const paymentRequired = !!master.requireRecurringPayment;
                    const isToggling =
                      requirePaymentMutation.isPending &&
                      requirePaymentMutation.variables?.userId === masterId;

                    return (
                      <div
                        key={masterId}
                        className="border border-gray-200 rounded-lg p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="bg-primary-100 p-3 rounded-full shrink-0">
                            <FiTrendingUp className="h-6 w-6 text-primary-600" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-lg font-bold text-gray-900 break-anywhere">{master.name}</h4>
                            <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                              <FiMail className="h-4 w-4 shrink-0" />
                              <span className="break-anywhere min-w-0">{master.email}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                          {/* <label className="flex items-center gap-3 cursor-pointer select-none">
                            <span className="text-sm text-gray-700 whitespace-nowrap">
                              Require $299/mo
                            </span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={paymentRequired}
                              disabled={isToggling}
                              onClick={() =>
                                requirePaymentMutation.mutate({
                                  userId: masterId,
                                  enabled: !paymentRequired,
                                })
                              }
                              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                                paymentRequired ? 'bg-primary-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                  paymentRequired ? 'translate-x-5' : 'translate-x-0.5'
                                }`}
                              />
                            </button>
                          </label> */}
                          <button
                            onClick={() => handleDemoteMaster(masterId, master.name)}
                            disabled={demoteMasterMutation.isPending}
                            className="btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto"
                          >
                            {demoteMasterMutation.isPending && demoteMasterMutation.variables === masterId ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-600"></div>
                                <span>Demoting...</span>
                              </>
                            ) : (
                              <>
                                <FiArrowDown className="h-4 w-4" />
                                <span>Demote to Follower</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Create New Master Tab */}
          {activeTab === 'create' && (
            <div className="max-w-md mx-auto">
              <div className="text-center mb-6">
                <div className="bg-primary-100 p-4 rounded-full inline-block mb-3">
                  <FiUserPlus className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Create New Master Trader</h3>
                <p className="text-gray-600">This will create a new account with master privileges</p>
              </div>

              {error && <ErrorDisplay error={error} onDismiss={() => setError('')} className="mb-4" />}

              <form onSubmit={handleCreateNewMaster} className="space-y-4">
                <SimpleFormField
                  label="Full Name"
                  name="name"
                  icon={<FiUser className="h-5 w-5 text-gray-400" />}
                  value={newMasterForm.name}
                  onChange={(e) => setNewMasterForm({ ...newMasterForm, name: e.target.value })}
                  required
                  placeholder="John Doe"
                />

                <SimpleFormField
                  label="Email Address"
                  name="email"
                  type="email"
                  icon={<FiMail className="h-5 w-5 text-gray-400" />}
                  value={newMasterForm.email}
                  onChange={(e) => setNewMasterForm({ ...newMasterForm, email: e.target.value })}
                  required
                  placeholder="john@example.com"
                />

                <SimpleFormField
                  label="Password"
                  name="password"
                  type="password"
                  icon={<FiLock className="h-5 w-5 text-gray-400" />}
                  value={newMasterForm.password}
                  onChange={(e) => setNewMasterForm({ ...newMasterForm, password: e.target.value })}
                  required
                  placeholder="Minimum 6 characters"
                  showPasswordToggle={true}
                />

                {/* Trading Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Trading Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Swing Trading */}
                    <button
                      type="button"
                      onClick={() => setSelectedTradingType('swing')}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${selectedTradingType === 'swing'
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-300 hover:border-purple-400 bg-white'
                        }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <FiCalendar className={`h-5 w-5 mt-0.5 ${selectedTradingType === 'swing' ? 'text-purple-600' : 'text-gray-400'}`} />
                        <div>
                          <h4 className={`font-bold ${selectedTradingType === 'swing' ? 'text-purple-600' : 'text-gray-900'}`}>
                            Swing Trading
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">Long term trades</p>
                        </div>
                      </div>
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <FiDollarSign className="h-3 w-3" />
                          <span>Min: <strong>$20,000</strong></span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FiClock className="h-3 w-3" />
                          <span>Days to weeks</span>
                        </div>
                      </div>
                      {selectedTradingType === 'swing' && (
                        <div className="mt-2 flex items-center gap-1 text-purple-600 font-medium text-sm">
                          <FiCheck className="h-4 w-4" />
                          <span>Selected</span>
                        </div>
                      )}
                    </button>

                    {/* Day Trading */}
                    <button
                      type="button"
                      onClick={() => setSelectedTradingType('day')}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${selectedTradingType === 'day'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-400 bg-white'
                        }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <FiClock className={`h-5 w-5 mt-0.5 ${selectedTradingType === 'day' ? 'text-blue-600' : 'text-gray-400'}`} />
                        <div>
                          <h4 className={`font-bold ${selectedTradingType === 'day' ? 'text-blue-600' : 'text-gray-900'}`}>
                            Day Trading / Hedge Trading
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">Short term trades</p>
                        </div>
                      </div>
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <FiDollarSign className="h-3 w-3" />
                          <span>Min: <strong>$1k-$2k</strong></span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FiClock className="h-3 w-3" />
                          <span>Few hours max</span>
                        </div>
                      </div>
                      {selectedTradingType === 'day' && (
                        <div className="mt-2 flex items-center gap-1 text-blue-600 font-medium text-sm">
                          <FiCheck className="h-4 w-4" />
                          <span>Selected</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={createMasterMutation.isPending || !selectedTradingType}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMasterMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <FiUserPlus className="h-5 w-5" />
                      <span>Create Master Trader</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Upgrade Follower Tab */}
          {activeTab === 'upgrade' && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-6">
                <div className="bg-primary-100 p-4 rounded-full inline-block mb-3">
                  <FiPlus className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Upgrade Follower to Master</h3>
                <p className="text-gray-600">Select an existing follower to upgrade to master trader</p>
              </div>

              {error && <ErrorDisplay error={error} onDismiss={() => setError('')} className="mb-4" />}


              {/* Search and Pagination for Upgrade Tab */}
              <div className="mb-4">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPage(1); // Reset to first page on search
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 "
                    style={{ color: 'black' }}
                  />
                </div>
              </div>

              {usersLoading ? (
                <Loader message="Loading followers..." />
              ) : followers.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                  <FiUser className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No followers found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {followers.map((user: User) => {
                    const userId = user._id || user.id;
                    const isSelected = selectedUserId === userId;

                    return (
                      <div
                        key={userId}
                        onClick={() => setSelectedUserId(userId!)}
                        className={`border rounded-lg p-4 cursor-pointer transition-all ${isSelected
                          ? 'border-primary-600 bg-primary-600 text-white shadow-md transform scale-[1.01]'
                          : 'border-gray-200 hover:border-primary-300 bg-white text-gray-900 hover:shadow-sm'
                          }`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className={`p-2 rounded-full shrink-0 ${isSelected ? 'bg-white/20' : 'bg-gray-100'}`}>
                            <FiUser className={`h-5 w-5 ${isSelected ? 'text-white' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-bold break-anywhere ${isSelected ? 'text-white' : 'text-gray-900'}`}>{user.name}</h4>
                            <p className={`text-sm break-anywhere ${isSelected ? 'text-primary-100' : 'text-gray-500'}`}>{user.email}</p>
                          </div>
                          {isSelected && (
                            <FiCheckCircle className="h-6 w-6 text-white shrink-0" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination Controls */}
              {!usersLoading && followers.length > 0 && pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
                  <span>
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={pagination.page === 1}
                      className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                      disabled={pagination.page === pagination.pages}
                      className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Trading Type Selection for Upgrade */}
              {selectedUserId && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Trading Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Swing Trading */}
                    <button
                      type="button"
                      onClick={() => setUpgradeTradingType('swing')}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${upgradeTradingType === 'swing'
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-300 hover:border-purple-400 bg-white'
                        }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <FiCalendar className={`h-5 w-5 mt-0.5 ${upgradeTradingType === 'swing' ? 'text-purple-600' : 'text-gray-400'}`} />
                        <div>
                          <h4 className={`font-bold ${upgradeTradingType === 'swing' ? 'text-purple-600' : 'text-gray-900'}`}>
                            Swing Trading
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">Long term trades</p>
                        </div>
                      </div>
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <FiDollarSign className="h-3 w-3" />
                          <span>Min: <strong>$20,000</strong></span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FiClock className="h-3 w-3" />
                          <span>Days to weeks</span>
                        </div>
                      </div>
                      {upgradeTradingType === 'swing' && (
                        <div className="mt-2 flex items-center gap-1 text-purple-600 font-medium text-sm">
                          <FiCheck className="h-4 w-4" />
                          <span>Selected</span>
                        </div>
                      )}
                    </button>

                    {/* Day Trading */}
                    <button
                      type="button"
                      onClick={() => setUpgradeTradingType('day')}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${upgradeTradingType === 'day'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-400 bg-white'
                        }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <FiClock className={`h-5 w-5 mt-0.5 ${upgradeTradingType === 'day' ? 'text-blue-600' : 'text-gray-400'}`} />
                        <div>
                          <h4 className={`font-bold ${upgradeTradingType === 'day' ? 'text-blue-600' : 'text-gray-900'}`}>
                            Day Trading / Hedge Trading
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">Short term trades</p>
                        </div>
                      </div>
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <FiDollarSign className="h-3 w-3" />
                          <span>Min: <strong>$1k-$2k</strong></span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FiClock className="h-3 w-3" />
                          <span>Few hours max</span>
                        </div>
                      </div>
                      {upgradeTradingType === 'day' && (
                        <div className="mt-2 flex items-center gap-1 text-blue-600 font-medium text-sm">
                          <FiCheck className="h-4 w-4" />
                          <span>Selected</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={handleUpgradeUser}
                disabled={!selectedUserId || !upgradeTradingType || createMasterMutation.isPending}
                className="btn-primary w-full flex items-center justify-center gap-2 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMasterMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    <span>Upgrading...</span>
                  </>
                ) : (
                  <>
                    <FiTrendingUp className="h-5 w-5" />
                    <span>Upgrade to Master</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {showConfirmModal && confirmAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              {confirmAction === 'demote' ? 'Confirm Demote' : 'Confirm Upgrade'}
            </h3>
            <p className="text-gray-600 mb-6">
              {confirmAction === 'demote'
                ? <>Are you sure you want to demote <strong className="text-gray-900">{confirmUserName}</strong> from Master to Follower?</>
                : <>Are you sure you want to upgrade <strong className="text-gray-900">{confirmUserName}</strong> to Master?</>}
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                  setConfirmUserId('');
                  setConfirmUserName('');
                }}
                disabled={demoteMasterMutation.isPending || createMasterMutation.isPending}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={demoteMasterMutation.isPending || createMasterMutation.isPending}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${confirmAction === 'demote' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:opacity-90'}`}
              >
                {demoteMasterMutation.isPending || createMasterMutation.isPending ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterManagementModal;

